<template>
    <div class="form-control datepicker-input">
        <input
            v-bind="$attrs"
            ref="datePicker"
            type="date"
            class="datepicker-input"
            :class="{ '!cursor-not-allowed': disabled }"
            :disabled="disabled"
            :value="modelDate"
            :min="min"
            :max="max"
            :placeholder="disablePlaceholder ? null : placeholder"
            @focus="$event.currentTarget.showPicker()"
            @keydown="$event.preventDefault()"
            @change="onChange"
            @blur="onClose"
        />
    </div>
</template>

<script>
import { format, isValid, isMatch, parse, parseISO } from 'date-fns';

export default {
    inheritAttrs: false,
    emits: ['input', 'change', 'dp-change', 'close'],
    props: {
        modelValue: {
            required: true,
            default: null,
            validator(value) {
                return value === null ||
                    isValid(value) ||
                    isMatch(value, 'dd.MM.yyyy');
            },
        },
        config: {
            type: Object,
            default: null,
        },
        placeholder: {
            type: String,
            default: () => format(new Date(), 'dd.MM.yyyy'),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disablePlaceholder: {
            type: Boolean,
            default: false,
        },
    },

    watch: {
        config: {
            deep: true,
            handler(newConfig) {
                if (newConfig.minDate) {
                    this.setMinDate(newConfig.minDate);
                }
                if (newConfig.maxDate) {
                    this.setMaxDate(newConfig.maxDate);
                }
            },
        },
    },

    data() {
        return {
            inputFormat: 'dd.MM.yyyy',
            dateFormat: 'yyyy-MM-dd',
            min: null,
            max: null,
        };
    },

    computed: {
        modelDate() {
            if (this.modelValue instanceof Date) {
                if (!isValid(this.modelValue)) {
                    return null;
                }

                return format(this.modelValue, this.dateFormat);
            }

            return this.convertDateFormat(this.modelValue, this.inputFormat, this.dateFormat);
        },
    },

    methods: {
        convertDateFormat(date, sourceFormat, targetFormat) {
            if (date === null) return null;

            const parsedDate = sourceFormat === 'iso' ? parseISO(date) : parse(date, sourceFormat, new Date());
            if (!isValid(parsedDate)) {
                return null;
            }

            return format(parsedDate, targetFormat);
        },

        transformDate(date, dateFormat = this.dateFormat) {
            if (date === null) return null;

            if (date instanceof Date) {
                return format(date, dateFormat);
            } else if (isValid(parseISO(date))) {
                return format(parseISO(date), dateFormat);
            }
            return this.convertDateFormat(date, this.inputFormat, dateFormat);
        },

        setDate(date) {
            if (this.disablePlaceholder) return;

            this.$emit('update:model-value', this.transformDate(date, this.inputFormat));
        },

        setMinDate(minDate) {
            this.min = this.transformDate(minDate);
        },

        setMaxDate(maxDate) {
            this.max = this.transformDate(maxDate);
        },

        onClose(event) {
            const dateStr = this.convertDateFormat(event.target.value, this.dateFormat, this.inputFormat);
            this.$emit('close', dateStr);
            this.onChange(event);
        },

        onChange(event) {
            event.currentTarget.blur();

            const dateStr = this.convertDateFormat(event.target.value, this.dateFormat, this.inputFormat);
            if (dateStr === null) {
                return;
            }

            this.$emit('update:model-value', dateStr);
            this.$emit('input', dateStr);
            this.$emit('change', dateStr);
            this.$emit('dp-change', dateStr);
        },
    },
};
</script>

<style scoped>
.\!cursor-not-allowed {
    cursor: not-allowed !important;
}

input.datepicker-input {
    border: none;
    caret-color: #161c2d;
    width: 100%;
    height: 100%;
    min-width: 100px;
    word-wrap: normal;
}

.datepicker-overlay .datepicker-input {
    word-wrap: normal;
}
</style>
